import { ForumAPI } from "contexts/forum_api_context";

export class S3Bucket {
  private name: string;

  private region: string;

  private baseUrl: string;

  private forumAPI?: ForumAPI;

  constructor(bucket: { NAME: string; REGION: string }, forumAPI?: ForumAPI) {
    this.name = bucket.NAME;
    this.region = bucket.REGION;
    this.baseUrl = this._getBaseUrl();
    this.forumAPI = forumAPI;
  }

  getS3Url(objectKey = "") {
    return `${this.baseUrl}${objectKey}`;
  }

  async uploadToS3(objectKey: string, data: string | File): Promise<string> {
    try {
      const manifest = await this._fetchS3Manifest();
      const { mime_type, encoded_policy, signature, algorithm, credential, date } = manifest;
      const blob = new Blob([data], { type: mime_type });
      const formData = new FormData();
      formData.append("key", objectKey);
      formData.append("Content-Type", mime_type);
      formData.append("policy", encoded_policy);
      formData.append("x-amz-signature", signature);
      formData.append("x-amz-algorithm", algorithm);
      formData.append("x-amz-credential", credential);
      formData.append("x-amz-date", date);
      formData.append("file", blob);

      const response = await fetch(this.getS3Url(), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const msg = `There has been a problem with fetch operation: ${response.status} ${response.statusText}`;
        throw new Error(msg);
      }

      window.flightLogger.log(`Saved S3 file: ${this.getS3Url(objectKey)}`);
      return this.getS3Url(objectKey);
    } catch (err) {
      window.flightLogger.error("[S3Bucket]: Upload to S3 failed", err);
      throw err;
    }
  }

  _getBaseUrl() {
    /*
     * AWS is moving away from path-style and global endpoints, but
     * have yet to support virtual-style requests for buckets with "."
     * characters. More info:
     * https://aws.amazon.com/blogs/aws/amazon-s3-path-deprecation-plan-the-rest-of-the-story/
     *
     * Below are the standard endpoint formats to access an object in an S3 bucket:
     *     - Virtual-hosted style URL (preferred):
     *       https://bucket-name.s3.region-code.amazonaws.com/key-name
     *
     *     - Path-style URL:
     *       https://s3.region-code.amazonaws.com/bucket-name/key-name
     */

    return this.name.indexOf(".") === -1
      ? `https://${this.name}.s3.${this.region}.amazonaws.com/`
      : `https://s3.${this.region}.amazonaws.com/${this.name}/`;
  }

  async _fetchS3Manifest() {
    const url = `api/v1/s3_upload_manifest?bucket=${this.name}`;
    const response = await this.forumAPI?.get(url);
    return response?.json();
  }
}
